// src/services/subscribe.service.js
import { supabase } from "@/supabase";

export const activeSubscriptions = {};
let cleanupAttached = false;

// Keep Realtime's auth token in sync with the user's session. Without this,
// Realtime keeps using the access token captured at first subscribe time;
// once it expires (~1h) Postgres-Changes events silently stop arriving until
// the user reloads the page. The listener is attached once at module load.
let realtimeAuthListenerAttached = false;
function attachRealtimeAuthListener() {
  if (realtimeAuthListenerAttached) return;
  realtimeAuthListenerAttached = true;
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === "TOKEN_REFRESHED" && session?.access_token) {
      supabase.realtime.setAuth(session.access_token);
    }
  });
}
attachRealtimeAuthListener();

/**
 * Wait until we actually have a valid authenticated session
 */
async function waitForSession() {
  let { data: { session } } = await supabase.auth.getSession();

  // If already logged in
  if (session && session.access_token) {
    return session;
  }

  // Otherwise wait for auth state to emit
  return new Promise((resolve) => {
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (newSession && newSession.access_token) {
        subscription.unsubscribe();
        resolve(newSession);
      }
    });
  });
}

/**
 * Subscribe to a Postgres table
 */
// Channel topics must be unique per subscription. Reusing `channel_<table>`
// means that if a previous channel is still tearing down, supabase hands back
// that already-subscribed instance and calling .on() on it throws
// "cannot add postgres_changes callbacks ... after subscribe()".
let channelSeq = 0;

export async function subscribeTable(table, callback) {
  if (activeSubscriptions[table]) {
    console.log(`[Supabase] Already subscribed to ${table}`);
    return activeSubscriptions[table];
  }

  // Claim the slot synchronously. The old code only registered the channel in
  // the SUBSCRIBED callback, so two mounts in quick succession (switching from
  // Email to Chats, which both subscribe to the same tables) would both get
  // past the guard and build duplicate channels.
  const pending = { pending: true };
  activeSubscriptions[table] = pending;

  let session;
  try {
    session = await waitForSession();
  } catch (e) {
    if (activeSubscriptions[table] === pending) delete activeSubscriptions[table];
    throw e;
  }
  // Unsubscribed (screen unmounted) while we were waiting for the session.
  if (activeSubscriptions[table] !== pending) return activeSubscriptions[table] || null;

  supabase.realtime.setAuth(session.access_token);

  const channel = supabase
    .channel(`channel_${table}_${++channelSeq}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table },
      (payload) => {
        // console.log(`[Supabase] Realtime change on ${table}:`, payload);
        callback(payload);
      }
    )
    .subscribe((status) => {
      console.log(`[Supabase] Subscription for ${table}:`, status);

      if (status === "TIMED_OUT" || status === "CHANNEL_ERROR") {
        console.warn(`[Supabase] Subscription for ${table} failed (${status}), retrying...`);
        // Free the slot and drop the dead channel before retrying, otherwise
        // the retry hits the guard above and silently does nothing.
        if (activeSubscriptions[table] === channel) delete activeSubscriptions[table];
        supabase.removeChannel(channel);
        setTimeout(() => subscribeTable(table, callback), 2000);
      }
    });

  activeSubscriptions[table] = channel;

  if (!cleanupAttached) {
    window.addEventListener("beforeunload", function () {
      unSubscribeAll();
    });
    cleanupAttached = true;
  }

  return channel;
}

/**
 * Subscribe to a table on a UNIQUE channel (not deduped by table name), so multiple
 * components can each listen independently. Returns the channel; pass it to unsubscribeChannel().
 */
export async function subscribeTo(channelName, table, callback) {
  const session = await waitForSession();
  supabase.realtime.setAuth(session.access_token);
  const channel = supabase
    .channel(channelName)
    .on("postgres_changes", { event: "*", schema: "public", table }, (payload) => callback(payload))
    .subscribe();
  return channel;
}

/** Remove a channel created by subscribeTo(). */
export function unsubscribeChannel(channel) {
  if (channel) setTimeout(() => supabase.removeChannel(channel), 100);
}

/**
 * Unsubscribe from a single table
 */
export function unSubscribeTable(table) {
  const channel = activeSubscriptions[table];
  if (!channel) return;
  // Free the slot immediately so a screen mounting right after this one can
  // subscribe cleanly. Topics are unique per subscription, so removing the old
  // channel synchronously can't clash with the new one.
  delete activeSubscriptions[table];
  if (channel.pending) return;            // never got as far as a real channel
  supabase.removeChannel(channel);
  console.log(`[Supabase] Unsubscribed from ${table}`);
}

/**
 * Unsubscribe from all tables
 */
export function unSubscribeAll() {
  Object.keys(activeSubscriptions).forEach((table) => unSubscribeTable(table));
}
